/**
 * Install an available component onto a vacant equipment position (W3.3 API).
 */

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Form, Modal, Select, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import pmvService from '@/services/pmv.service';

const { Text } = Typography;

export type InstallTargetPosition = {
  position_id: string;
  position_code: string;
  display_label?: string | null;
  family?: string | null;
};

export const PmvInstallToPositionModal: React.FC<{
  open: boolean;
  profileId: string;
  position: InstallTargetPosition | null;
  family: 'TYRE' | 'BATTERY';
  onClose: () => void;
  onInstalled?: () => void;
}> = ({ open, profileId, position, family, onClose, onInstalled }) => {
  const [componentId, setComponentId] = useState<string | undefined>();

  const availQ = useQuery({
    queryKey: ['pmv-components-available', family],
    queryFn: () =>
      pmvService.listComponents({
        family,
        available_only: true,
        pageSize: 100,
      }),
    enabled: open,
    staleTime: 15_000,
  });

  const options = useMemo(
    () =>
      (availQ.data?.items || []).map((c) => ({
        value: c.id,
        label: `${c.component_number}${c.condition_code ? ` · ${c.condition_code}` : ''}${
          c.definition_name ? ` — ${c.definition_name}` : ''
        }`,
      })),
    [availQ.data]
  );

  const installMut = useMutation({
    mutationFn: () => {
      if (!componentId || !position) throw new Error('Select a component');
      return pmvService.installComponent({
        component_id: componentId,
        pmv_profile_id: profileId,
        equipment_position_id: position.position_id,
        position_code: position.position_code,
        idempotency_key: `install:${profileId}:${position.position_id}:${componentId}`,
      });
    },
    onSuccess: () => {
      message.success(`${family === 'TYRE' ? 'Tyre' : 'Battery'} installed on ${position?.position_code}`);
      setComponentId(undefined);
      onClose();
      onInstalled?.();
    },
    onError: (e: unknown) => {
      const ax = e as { message?: string; response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || ax.message || 'Install failed');
    },
  });

  return (
    <Modal
      title={`Install ${family === 'TYRE' ? 'tyre' : 'battery'} @ ${position?.position_code || ''}`}
      open={open && Boolean(position)}
      onCancel={() => {
        setComponentId(undefined);
        onClose();
      }}
      onOk={() => installMut.mutate()}
      okText="Install"
      okButtonProps={{ disabled: !componentId }}
      confirmLoading={installMut.isPending}
      destroyOnClose
      width={480}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`${position?.display_label || position?.position_code || 'Position'} is vacant`}
        description="Fits an available registered component. Does not change Inventory stock quantity."
      />
      <Form layout="vertical">
        <Form.Item
          label={`Available ${family === 'TYRE' ? 'tyres' : 'batteries'}`}
          required
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Need a new unit? Register it first in{' '}
              <Link to={`/asset/pmv/components?family=${family}`}>Component Register</Link>.
            </Text>
          }
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={availQ.isLoading ? 'Loading…' : 'Select component'}
            options={options}
            value={componentId}
            onChange={setComponentId}
            notFoundContent={
              availQ.isLoading ? (
                'Loading…'
              ) : (
                <span>
                  No available {family === 'TYRE' ? 'tyres' : 'batteries'}.{' '}
                  <Link to={`/asset/pmv/components?family=${family}`}>Register one</Link>
                </span>
              )
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PmvInstallToPositionModal;
